// Pulls "what will be stopped" data from an application's record so the
// move-with-automation modal can render the current-stage section.
//
// Source of truth: BE adds `automation` to each `ats_job_stage_action`
// on the application show response, keyed by ats_job_stage_action_id.
// Non-terminal statuses (pending/scheduled/waiting/running) mean the
// action would have fired automatically — moving the candidate cancels
// those executions, so we surface them as "Will be stopped".
import { MOVE_WITH_AUTOMATION_STRINGS } from './moveWithAutomation.strings';

type StageActionLike = {
  id?: number | string;
  kind?: string;
  label?: string;
  automation?: { status?: string } | null;
};

type JobStageLike = {
  id?: number | string;
  name?: string;
  ats_job_stage_actions?: StageActionLike[];
};

type ApplicationLike = {
  job_stage_id?: number | string;
  ats_job_stages?: JobStageLike[];
};

export type StoppingAction = {
  id: number | string;
  kind: string;
  label: string;
};

// Non-terminal automation statuses — these executions get cancelled
// when the candidate leaves the stage, so they should appear in the
// "Will be stopped" list.
const STOPPING_STATUSES = new Set([
  'pending',
  'scheduled',
  'waiting',
  'running',
]);

export type DerivedCurrentStageStopping = {
  currentStageName?: string;
  currentStageStoppingActions: StoppingAction[];
};

export const deriveCurrentStageStopping = (
  application: ApplicationLike | null | undefined
): DerivedCurrentStageStopping => {
  if (
    !application?.ats_job_stages ||
    application.job_stage_id === undefined ||
    application.job_stage_id === null
  ) {
    return { currentStageStoppingActions: [] };
  }
  const currentStage = application.ats_job_stages.find(
    (s) => String(s?.id) === String(application.job_stage_id)
  );
  if (!currentStage) {
    return { currentStageStoppingActions: [] };
  }

  const stoppingActions: StoppingAction[] = (
    currentStage.ats_job_stage_actions ?? []
  )
    .filter((a) => {
      const status = a?.automation?.status;
      return Boolean(status && STOPPING_STATUSES.has(status));
    })
    .map((a) => ({
      id: a?.id ?? '',
      kind: a?.kind ?? '',
      label:
        a?.label ??
        a?.kind ??
        MOVE_WITH_AUTOMATION_STRINGS.fallback.activityLabel,
    }));

  return {
    currentStageName: currentStage.name,
    currentStageStoppingActions: stoppingActions,
  };
};

// Fallback when the stage has an active rule but no executions yet —
// lists the rule's configured activities instead.
type RuleLike = {
  actions?: Array<{
    id?: number;
    position?: number;
    target?: { kind?: string; label?: string } | null;
  }>;
};

export const ruleActionsToStoppingActions = (
  rule: RuleLike | null | undefined
): StoppingAction[] =>
  (rule?.actions ?? []).map((action) => ({
    id: action.id ?? action.position ?? '',
    kind: action.target?.kind ?? '',
    label:
      action.target?.label ??
      action.target?.kind ??
      MOVE_WITH_AUTOMATION_STRINGS.fallback.activityLabel,
  }));
